import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme } from './hooks/useTheme';
import ProductGridView from './components/ProductGridView';
import FeaturedProductsCarousel from './components/FeaturedProductsCarousel';
import SimpleAddressForm from './components/SimpleAddressForm';
import { 
  ArrowLeft, 
  Search,
  ChevronRight,
  X, 
  Plus, 
  Minus, 
  Trash2, 
  Home, 
  FileText, 
  Tag, 
  ShoppingCart,
  Truck,
  Store,
  ChevronDown,
  ChevronUp,
  Package,
  DollarSign,
  Clock,
  Star,
  CheckCircle,
  Copy,
  XCircle,
  Check,
  MapPin
} from 'lucide-react';
import { Switch } from './components/ui/switch';
import PixCheckoutModal from './components/PixCheckoutModal';
import MPCardCheckout from './MPCardCheckout';

// Determinar URL do backend - mesma lógica do App.js
const getBackendUrl = () => {
  if (typeof window !== 'undefined') {
    const currentHost = window.location.hostname;
    if (currentHost === 'pedeefacil.com.br' || currentHost === 'www.pedeefacil.com.br') {
      return 'https://pedeefacil.com.br';
    }
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
};

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// ============================================================
// Sub-componentes/helpers extraidos (Fase 2 refactor) -> components/publicmenu/
// ============================================================
import { applyShippingPromo, FreeShippingProgress } from './components/publicmenu/shipping';
import NeighborhoodSelector from './components/publicmenu/NeighborhoodSelector';
import { ProductSkeleton } from './components/publicmenu/skeletons';
import LazyImage from './components/publicmenu/LazyImage';
import BottomNav from './components/publicmenu/BottomNav';
import Header from './components/publicmenu/Header';
import HomeScreen from './components/publicmenu/HomeScreen';
import CartScreen from './components/publicmenu/CartScreen';
import ProductScreen from './components/publicmenu/ProductScreen';
import CheckoutScreen from './components/publicmenu/CheckoutScreen';
import PromosScreen from './components/publicmenu/PromosScreen';
import OrdersHistoryScreen from './components/publicmenu/OrdersHistoryScreen';
import OrderConfirmationScreen from './components/publicmenu/OrderConfirmationScreen';
import ClosedStoreModal from './components/publicmenu/ClosedStoreModal';
import MinimumOrderModal from './components/publicmenu/MinimumOrderModal';
import CartConfirmationModal from './components/publicmenu/CartConfirmationModal';
import SchedulingModal from './components/publicmenu/SchedulingModal';
import OrderDetailsModal from './components/publicmenu/OrderDetailsModal';
import SearchModal from './components/publicmenu/SearchModal';
import usePublicMenuCart from './hooks/usePublicMenuCart';
import usePublicMenuCoupon from './hooks/usePublicMenuCoupon';
import { isIOS, openWhatsApp, arraysAreEqual, getNextOpeningInfo } from './utils/publicMenuHelpers';
// Re-export para preservar a API publica do modulo (antes eram exports nomeados daqui)
export { applyShippingPromo, FreeShippingProgress };

const PublicMenu = ({ subdomain = 'yurelanches' }) => {
  // 🎨 Load and apply theme with custom colors from backend
  const { theme, loading: themeLoading } = useTheme(subdomain);
  
  // 📐 Load layout preference from backend
  const [layout, setLayout] = useState('list');
  
  // 🔍 Search functionality
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const searchTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);
  
  const [currentScreen, setCurrentScreen] = useState('home');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [expandedSection, setExpandedSection] = useState(false);
  const [expandedImage, setExpandedImage] = useState(false); // Estado para imagem expandida
  const [checkoutStep, setCheckoutStep] = useState(1);
  const [deliveryType, setDeliveryType] = useState('delivery');
  const [address, setAddress] = useState({
    cep: '',
    neighborhood: '',
    street: '',
    number: '',
    complement: '',
    city: '',
    state: '',
    lat: null,
    lng: null
  });
  const [loadingCep, setLoadingCep] = useState(false); // Loading da busca de CEP
  const [paymentMethod, setPaymentMethod] = useState('dinheiro');
  const [needsChange, setNeedsChange] = useState(false);
  const [changeAmount, setChangeAmount] = useState('');
  const [pixCopied, setPixCopied] = useState(false);
  // Mantém a posição de scroll do checkout entre re-renders/remounts (evita "pulo" pro topo)
  const checkoutScrollTopRef = useRef(0);
  // Mantém a posição de scroll do cardápio (home) ao entrar/sair da tela de produto/carrinho/etc.
  const homeScrollTopRef = useRef(0);
  const [showCartConfirmation, setShowCartConfirmation] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all');
  const [showStoreProfile, setShowStoreProfile] = useState(false);
  const [showClosedStoreModal, setShowClosedStoreModal] = useState(false);
  const [showMinOrderModal, setShowMinOrderModal] = useState(false); // Modal de valor mínimo
  const [showPixCheckout, setShowPixCheckout] = useState(false);
  const [pixOrderData, setPixOrderData] = useState(null);
  const [showCardCheckout, setShowCardCheckout] = useState(false);
  const [cardOrderData, setCardOrderData] = useState(null);
  
  // 🎉 Payment Success Screen states
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [successOrderData, setSuccessOrderData] = useState(null);
  
  // ✅ Order Confirmation Screen states (nova tela de confirmação)
  const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
  const [confirmationOrderData, setConfirmationOrderData] = useState(null);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  
  // 📅 Scheduling states (Agendamento)
  const [showSchedulingModal, setShowSchedulingModal] = useState(false);
  const [schedulingSlots, setSchedulingSlots] = useState([]);
  const [selectedScheduleDate, setSelectedScheduleDate] = useState(null);
  const [selectedScheduleTime, setSelectedScheduleTime] = useState(null);
  const [isScheduledOrder, setIsScheduledOrder] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [schedulingModalShown, setSchedulingModalShown] = useState(false); // Controle para evitar re-exibição
  
  // Cashback states
  const [customerPhone, setCustomerPhone] = useState('');
  const [cashbackData, setCashbackData] = useState(null);
  const [showPhoneInput, setShowPhoneInput] = useState(false);
  const [useCashback, setUseCashback] = useState(false);
  const [cashbackAmount, setCashbackAmount] = useState(0);
  
  // 🎟️ Coupon states (moved to usePublicMenuCoupon hook)
  const [hasActiveCoupons, setHasActiveCoupons] = useState(false);
  const [showCouponInput, setShowCouponInput] = useState(false);
  
  // Real data from backend
  const [restaurant, setRestaurant] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);  // Produtos em destaque
  const [additionals, setAdditionals] = useState([]);
  const [additionalGroups, setAdditionalGroups] = useState([]);
  const [deliveryZones, setDeliveryZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Delivery config (modo radius ou neighborhood)
  const [deliveryConfig, setDeliveryConfig] = useState({
    mode: 'neighborhood', // 'neighborhood' ou 'radius'
    radius_zones: [],
    max_delivery_radius: null,
    has_store_address: false
  });
  const [calculatedFee, setCalculatedFee] = useState(null); // Taxa calculada por raio
  const [calculatingFee, setCalculatingFee] = useState(false); // Loading do cálculo
  const [feeError, setFeeError] = useState(''); // Erro no cálculo da taxa
  // NOVO: Estado do resultado de entrega para o SimpleAddressForm (evita perda em re-render)
  const [addressDeliveryResult, setAddressDeliveryResult] = useState(null);
  
  // Ref para tracking do último endereço calculado (evita re-cálculos desnecessários)
  const lastCalculatedAddressRef = useRef('');
  
  // Customer info states
  const [customerName, setCustomerName] = useState('');
  const [customerPhoneInput, setCustomerPhoneInput] = useState('');
  const [orderHistory, setOrderHistory] = useState([]);
  const [toastMessage, setToastMessage] = useState(null);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);

  // Refs para inputs (uncontrolled components)
  const customerNameRef = useRef(null);
  const customerPhoneRef = useRef(null);
  const cepRef = useRef(null); // Ref para CEP
  const streetRef = useRef(null);
  const numberRef = useRef(null);
  const complementRef = useRef(null);
  const neighborhoodRef = useRef(null); // Ref para bairro no modo radius
  const cashbackPhoneRef = useRef(null);
  const cashbackAmountRef = useRef(null);
  const changeAmountRef = useRef(null);

  // ========== SISTEMA DE NAVEGAÇÃO COM HISTORY API ==========
  // Permite usar o botão "voltar" do navegador/celular para navegar entre telas
  
  // Ref para controlar se devemos ignorar o próximo popstate (evita loops)
  const isNavigatingRef = useRef(false);
  // Ref para armazenar a tela anterior (para lógica de voltar)
  const previousScreenRef = useRef('home');

  // 🛒 Carrinho (estado + handlers) via custom hook
  const {
    cart,
    setCart,
    addToCart,
    updateCartItemQuantity,
    clearCart,
    getCartTotal,
    getCartItemsCount
  } = usePublicMenuCart({ restaurant, setShowClosedStoreModal, setShowCartConfirmation });

  // 🎟️ Cupom (estado + handlers) via custom hook
  const {
    couponCode,
    setCouponCode,
    appliedCoupon,
    setAppliedCoupon,
    couponDiscount,
    setCouponDiscount,
    couponLoading,
    setCouponLoading,
    couponError,
    setCouponError,
    showCouponSuccess,
    setShowCouponSuccess,
    validateCoupon,
    removeCoupon
  } = usePublicMenuCoupon({ API, subdomain, customerPhone, getCartTotal });

  // Função para navegar para uma tela (wrapper do setCurrentScreen)
  const navigateToScreen = useCallback((screen, productData = null) => {
    // Salvar tela anterior
    previousScreenRef.current = currentScreen;

    // Se estamos saindo da home, capturar a posição de scroll atual do cardápio
    // para restaurar exatamente onde o cliente parou quando ele voltar.
    if (currentScreen === 'home' && screen !== 'home') {
      const el = document.getElementById('home-scroll-container');
      if (el) {
        homeScrollTopRef.current = el.scrollTop;
      }
    }

    // Marcar que estamos navegando programaticamente
    isNavigatingRef.current = true;
    
    // Atualizar estado da tela
    setCurrentScreen(screen);
    
    // Se for tela de produto, também setar o produto
    if (screen === 'product' && productData) {
      setSelectedProduct(productData);
    }
    
    // Adicionar entrada no histórico do navegador
    const stateData = { 
      screen, 
      timestamp: Date.now(),
      productId: productData?.id || null
    };
    
    // Usar hash para manter compatibilidade e dar feedback visual
    const hash = screen === 'home' ? '' : `#${screen}`;
    
    try {
      window.history.pushState(stateData, '', `${window.location.pathname}${hash}`);
    } catch (e) {
      console.warn('History API não disponível:', e);
    }
    
    // Reset flag após pequeno delay
    setTimeout(() => {
      isNavigatingRef.current = false;
    }, 100);
  }, [currentScreen]);
  
  // Handler para o evento popstate (botão voltar do navegador)
  useEffect(() => {
    const handlePopState = (event) => {
      // Ignorar se estamos navegando programaticamente
      if (isNavigatingRef.current) return;
      
      // Determinar para qual tela voltar
      let targetScreen = 'home';
      
      if (event.state?.screen) {
        // Se há estado salvo, usar ele
        targetScreen = event.state.screen;
      } else {
        // Sem estado = voltar para home ou tela anterior lógica
        const currentHash = window.location.hash.replace('#', '');
        if (currentHash && ['product', 'cart', 'checkout', 'promos', 'orders'].includes(currentHash)) {
          targetScreen = currentHash;
        } else {
          // Lógica de voltar baseada na tela atual
          switch (currentScreen) {
            case 'product':
              targetScreen = 'home';
              break;
            case 'cart':
              targetScreen = 'home';
              break;
            case 'checkout':
              targetScreen = 'cart';
              break;
            case 'promos':
            case 'orders':
              targetScreen = 'home';
              break;
            default:
              targetScreen = 'home';
          }
        }
      }
      
      // Atualizar tela sem adicionar novo estado ao histórico
      setCurrentScreen(targetScreen);
      
      // Se voltou da tela de produto, limpar produto selecionado
      if (currentScreen === 'product' && targetScreen !== 'product') {
        setSelectedProduct(null);
      }
    };
    
    // Registrar listener
    window.addEventListener('popstate', handlePopState);
    
    // Configurar estado inicial no histórico (se ainda não existe)
    if (!window.history.state?.screen) {
      const initialHash = window.location.hash.replace('#', '');
      const initialScreen = initialHash && ['product', 'cart', 'checkout', 'promos', 'orders'].includes(initialHash) 
        ? initialHash 
        : 'home';
      
      try {
        window.history.replaceState(
          { screen: initialScreen, timestamp: Date.now() }, 
          '', 
          window.location.href
        );
      } catch (e) {
        console.warn('History API não disponível:', e);
      }
    }
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [currentScreen]);
  // ========== FIM DO SISTEMA DE NAVEGAÇÃO ==========

  // ========== PRESERVAÇÃO DE SCROLL DO CARDÁPIO ==========
  // Quando o usuário sai da home (ex: abre um produto), guardamos a posição atual.
  // Ao voltar para a home, restauramos exatamente onde parou (sem "pular" pro topo).
  useLayoutEffect(() => {
    if (currentScreen !== 'home') return;

    let cancelled = false;
    const target = homeScrollTopRef.current || 0;

    const restore = () => {
      if (cancelled) return;
      const el = document.getElementById('home-scroll-container');
      if (el) {
        el.scrollTop = target;
      }
    };

    // Restaura em múltiplos momentos para cobrir mount + primeiro paint + carregamento
    // assíncrono de imagens/produtos que alteram a altura do conteúdo.
    restore();
    const raf1 = requestAnimationFrame(() => {
      restore();
      requestAnimationFrame(restore);
    });
    const t1 = setTimeout(restore, 60);
    const t2 = setTimeout(restore, 200);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [currentScreen]);
  // ========== FIM DA PRESERVAÇÃO DE SCROLL ==========

  // Memoized functions
  const loadOrderHistory = React.useCallback(async (phone) => {
    if (!phone) return;
    
    try {
      const response = await axios.get(`${API}/public/orders/${subdomain}/${phone}`);
      const orders = Array.isArray(response.data) ? response.data : [];
      setOrderHistory(orders);

      // Fallback "endereço entre dispositivos": se localStorage não tem endereço
      // mas o cliente tem pedidos anteriores, pré-preenche a partir do último pedido
      const hasLocalAddress = !!(
        localStorage.getItem('customer_neighborhood_zone_id') ||
        localStorage.getItem('customer_neighborhood') ||
        localStorage.getItem('customer_street')
      );
      if (!hasLocalAddress && orders.length > 0) {
        const lastWithAddr = orders.find(o => o && o.address && (
          o.address.street || o.address.neighborhood
        ));
        if (lastWithAddr) {
          const a = lastWithAddr.address || {};
          const zoneName = lastWithAddr.delivery_zone_name || a.neighborhood || '';
          setAddress(prev => ({
            ...prev,
            street: a.street || prev.street,
            number: a.number || prev.number,
            complement: a.complement || prev.complement,
            neighborhood: zoneName || prev.neighborhood,
            cep: a.cep || prev.cep,
            city: a.city || prev.city,
            state: a.state || prev.state,
          }));
          if (lastWithAddr.customer_name && !localStorage.getItem('customer_name')) {
            setCustomerName(lastWithAddr.customer_name);
          }
          if (a.street) localStorage.setItem('customer_street', a.street);
          if (a.number) localStorage.setItem('customer_number', a.number);
          if (a.complement) localStorage.setItem('customer_complement', a.complement);
          if (zoneName) localStorage.setItem('customer_neighborhood', zoneName);
          if (a.cep) localStorage.setItem('customer_cep', a.cep);
          if (a.city) localStorage.setItem('customer_city', a.city);
          if (a.state) localStorage.setItem('customer_state', a.state);
          if (lastWithAddr.customer_name) localStorage.setItem('customer_name', lastWithAddr.customer_name);
          console.log('🔁 Endereço restaurado a partir do último pedido do cliente:', zoneName);
        }
      }
    } catch (error) {
      console.error('Error loading order history:', error);
      setOrderHistory([]);
    }
  }, [subdomain]);

  const loadCustomerData = React.useCallback(() => {
    // Load customer data from localStorage silently
    const savedName = localStorage.getItem('customer_name');
    const savedPhone = localStorage.getItem('customer_phone');
    const savedStreet = localStorage.getItem('customer_street');
    const savedNumber = localStorage.getItem('customer_number');
    const savedComplement = localStorage.getItem('customer_complement');
    const savedNeighborhood = localStorage.getItem('customer_neighborhood');
    const savedNeighborhoodZoneId = localStorage.getItem('customer_neighborhood_zone_id');
    const savedCep = localStorage.getItem('customer_cep');
    const savedCity = localStorage.getItem('customer_city');
    const savedState = localStorage.getItem('customer_state');
    // NOVO: Carregar coordenadas salvas
    const savedLat = localStorage.getItem('customer_lat');
    const savedLng = localStorage.getItem('customer_lng');
    
    if (savedName) setCustomerName(savedName);
    if (savedPhone) {
      setCustomerPhone(savedPhone);
      setCustomerPhoneInput(savedPhone);
      // Load order history
      loadOrderHistory(savedPhone);
    }
    
    // Load address data (incluindo coordenadas)
    if (savedStreet || savedNumber || savedComplement || savedNeighborhood || savedCep) {
      setAddress({
        cep: savedCep || '',
        street: savedStreet || '',
        number: savedNumber || '',
        complement: savedComplement || '',
        neighborhood: savedNeighborhood || '',
        city: savedCity || '',
        state: savedState || '',
        // NOVO: Incluir coordenadas no estado do endereço
        lat: savedLat ? parseFloat(savedLat) : null,
        lng: savedLng ? parseFloat(savedLng) : null
      });
      
      // Log se encontrou coordenadas salvas
      if (savedLat && savedLng) {
        console.log('📍 Coordenadas restauradas do localStorage:', { lat: savedLat, lng: savedLng });
      }
      
      // Se tiver bairro salvo, encontrar a zona correspondente (por ID ou nome)
      if ((savedNeighborhood || savedNeighborhoodZoneId) && deliveryZones.length > 0) {
        // Priorizar busca por ID (mais confiável)
        let zone = savedNeighborhoodZoneId 
          ? deliveryZones.find(z => z.id === savedNeighborhoodZoneId)
          : null;
        
        // Fallback: buscar por nome
        if (!zone && savedNeighborhood) {
          zone = deliveryZones.find(z => z.name === savedNeighborhood);
        }
        
        if (zone) {
          setSelectedZone(zone);
          console.log('🏘️ Bairro restaurado do localStorage:', zone.name);
        }
      }
    }
  }, [loadOrderHistory, deliveryZones]);
  
  const saveCustomerData = React.useCallback((name, phone) => {
    // Save customer data silently - don't trigger re-render
    if (name) localStorage.setItem('customer_name', name);
    if (phone) localStorage.setItem('customer_phone', phone);
  }, []);
  
  const saveAddressData = React.useCallback((addressData) => {
    // Save address data silently
    if (addressData.street) localStorage.setItem('customer_street', addressData.street);
    if (addressData.number) localStorage.setItem('customer_number', addressData.number);
    if (addressData.complement !== undefined) {
      if (addressData.complement) {
        localStorage.setItem('customer_complement', addressData.complement);
      } else {
        localStorage.removeItem('customer_complement');
      }
    }
    if (addressData.neighborhood) localStorage.setItem('customer_neighborhood', addressData.neighborhood);
    // Salvar ID da zona de entrega para restaurar seleção corretamente
    if (addressData.neighborhoodZoneId) {
      localStorage.setItem('customer_neighborhood_zone_id', addressData.neighborhoodZoneId);
    }
    if (addressData.cep) localStorage.setItem('customer_cep', addressData.cep);
    if (addressData.city) localStorage.setItem('customer_city', addressData.city);
    if (addressData.state) localStorage.setItem('customer_state', addressData.state);
    // NOVO: Salvar coordenadas para evitar nova busca quando cliente retornar
    if (addressData.lat) localStorage.setItem('customer_lat', String(addressData.lat));
    if (addressData.lng) localStorage.setItem('customer_lng', String(addressData.lng));
  }, []);

  // NOVO: Limpar dados de endereço do localStorage quando usuário clica em "Alterar endereço"
  const clearAddressData = React.useCallback(() => {
    console.log('🧹 [PublicMenu] Limpando dados de endereço do localStorage');
    localStorage.removeItem('customer_street');
    localStorage.removeItem('customer_number');
    localStorage.removeItem('customer_complement');
    localStorage.removeItem('customer_neighborhood');
    localStorage.removeItem('customer_neighborhood_zone_id');
    localStorage.removeItem('customer_cep');
    localStorage.removeItem('customer_city');
    localStorage.removeItem('customer_state');
    localStorage.removeItem('customer_lat');
    localStorage.removeItem('customer_lng');
    
    // Limpar estado local também
    setAddress({
      cep: '',
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      lat: null,
      lng: null
    });
    setCalculatedFee(null);
    setFeeError(null);
    setSelectedZone(null);
    setAddressDeliveryResult(null); // Limpar resultado de entrega
  }, []);

  // Memoriza o initialAddress para o SimpleAddressForm para evitar re-renders
  // Só recalcula quando os campos de texto mudam
  const initialAddressForForm = useMemo(() => {
    return {
      cep: address.cep,
      street: address.street,
      number: address.number,
      complement: address.complement,
      neighborhood: address.neighborhood,
      city: address.city,
      state: address.state
      // NÃO incluir lat/lng para evitar re-render quando o form calcula
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Só recalcula quando os campos de texto mudam
    address.cep,
    address.street,
    address.number,
    address.neighborhood,
    address.city
  ]);

  // 📮 Buscar endereço pelo CEP (ViaCEP API)
  const fetchAddressByCep = React.useCallback(async (cep) => {
    // Limpar CEP (remover traços e espaços)
    const cleanCep = cep.replace(/\D/g, '');
    
    if (cleanCep.length !== 8) {
      return; // CEP incompleto
    }
    
    setLoadingCep(true);
    
    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cleanCep}/json/`);
      
      if (response.data && !response.data.erro) {
        const { logradouro, bairro, localidade, uf } = response.data;
        
        // Atualizar state com os dados do CEP
        setAddress(prev => ({
          ...prev,
          cep: cleanCep,
          street: logradouro || prev.street,
          neighborhood: bairro || prev.neighborhood,
          city: localidade || prev.city,
          state: uf || prev.state
        }));
        
        // Atualizar os refs (para inputs defaultValue)
        if (streetRef.current && logradouro) {
          streetRef.current.value = logradouro;
        }
        if (neighborhoodRef.current && bairro) {
          neighborhoodRef.current.value = bairro;
        }
        
        // Salvar no localStorage
        saveAddressData({
          cep: cleanCep,
          street: logradouro,
          neighborhood: bairro,
          city: localidade,
          state: uf
        });
        
        // Focar no campo número após preencher
        if (numberRef.current) {
          numberRef.current.focus();
        }
        
        toast.success('Endereço encontrado!');
      } else {
        toast.error('CEP não encontrado');
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      toast.error('Erro ao buscar CEP');
    } finally {
      setLoadingCep(false);
    }
  }, [saveAddressData]);

  // 🗺️ Calcular taxa de entrega por raio (quando modo = radius)
  const calculateDeliveryFeeByRadius = React.useCallback(async (forceCalculate = false) => {
    if (!address.street || !address.number || !address.neighborhood) {
      if (!forceCalculate) {
        setFeeError('');
        setCalculatedFee(null);
      }
      return;
    }

    setCalculatingFee(true);
    setFeeError('');

    try {
      // Usar cidade/estado do endereço se disponível, senão usar do restaurante
      const city = address.city || restaurant?.city || '';
      const state = address.state || restaurant?.state || '';
      const fullAddress = `${address.street}, ${address.number}, ${address.neighborhood}, ${city}, ${state}, Brasil`;
      
      const response = await axios.post(`${API}/delivery-config/calculate-fee`, {
        address: fullAddress,
        subdomain: subdomain,
        subtotal: getCartTotal(),
        delivery_type: 'delivery'
      });

      if (response.data.success) {
        setCalculatedFee({
          fee: response.data.delivery_fee,
          original_fee: response.data.original_fee ?? response.data.delivery_fee,
          promo_applied: response.data.promo_applied || null,
          amount_to_next_tier: response.data.amount_to_next_tier || 0,
          next_tier: response.data.next_tier || null,
          distance: response.data.distance_km,
          message: response.data.message
        });
        setFeeError('');
      } else {
        setFeeError(response.data.message || 'Endereço fora da área de entrega');
        setCalculatedFee(null);
      }
    } catch (error) {
      console.error('Erro ao calcular taxa:', error);
      setFeeError(error.response?.data?.detail || 'Erro ao calcular taxa de entrega');
      setCalculatedFee(null);
    } finally {
      setCalculatingFee(false);
    }
  }, [address, restaurant, subdomain]);

  // 🚀 Auto-calcular taxa quando endereço estiver completo (modo radius)
  // DESABILITADO: O SimpleAddressForm agora faz o cálculo internamente via onDeliveryFeeCalculated
  // Este useEffect foi substituído pelo componente SimpleAddressForm
  /*
  React.useEffect(() => {
    // Só calcular automaticamente no modo radius
    if (deliveryConfig.mode !== 'radius') return;
    // Só calcular se tiver endereço completo
    if (!address.street || !address.number || !address.neighborhood) return;
    
    // Criar uma key do endereço atual
    const currentAddressKey = `${address.street}|${address.number}|${address.neighborhood}`;
    
    // Se o endereço não mudou, não recalcular
    if (lastCalculatedAddressRef.current === currentAddressKey) return;
    
    // Debounce de 1000ms para evitar múltiplas chamadas durante digitação
    const timeoutId = setTimeout(() => {
      lastCalculatedAddressRef.current = currentAddressKey;
      calculateDeliveryFeeByRadius();
    }, 1000);
    
    return () => clearTimeout(timeoutId);
  }, [address.street, address.number, address.neighborhood, deliveryConfig.mode, calculateDeliveryFeeByRadius]);
  */

  const loadCashbackData = React.useCallback(async (phone) => {
    if (!phone || !restaurant) return;
    
    try {
      const response = await axios.get(`${API}/public/cashback/${subdomain}/${phone}`);
      setCashbackData(response.data);
      setCustomerPhone(phone);
    } catch (error) {
      console.error('Error loading cashback:', error);
      setCashbackData(null);
    }
  }, [restaurant, subdomain]);

  // 🚚 Auto-selecionar modalidade de pedido se apenas uma estiver habilitada
  useEffect(() => {
    if (!restaurant) return;
    
    const deliveryEnabled = restaurant.delivery_enabled !== false;
    const pickupEnabled = restaurant.pickup_enabled !== false;
    
    // Se apenas delivery está habilitado, selecionar automaticamente
    if (deliveryEnabled && !pickupEnabled) {
      setDeliveryType('delivery');
    }
    // Se apenas retirada está habilitada, selecionar automaticamente
    else if (!deliveryEnabled && pickupEnabled) {
      setDeliveryType('pickup');
    }
    // Se ambas estão habilitadas, manter o padrão (delivery)
    else if (deliveryEnabled && pickupEnabled) {
      setDeliveryType('delivery');
    }
  }, [restaurant]);

  // isIOS/openWhatsApp -> utils/publicMenuHelpers; validateCoupon/removeCoupon -> usePublicMenuCoupon



  // Load data on mount
  useEffect(() => {
    loadMenuData();
  }, [subdomain]);

  useEffect(() => {
    loadCustomerData();
  }, [loadCustomerData]);

  // Sincronizar selectedZone quando deliveryZones forem carregadas e houver bairro salvo
  // Usa comparação normalizada (trim/lowercase) para tolerar variações sutis (case, espaços)
  // e SEMPRE reescreve address.neighborhood + localStorage com o valor canônico da zona.
  useEffect(() => {
    if (deliveryZones.length === 0 || selectedZone) return;
    const normalize = (s) => String(s || '').trim().toLowerCase();
    const savedNeighborhood = localStorage.getItem('customer_neighborhood');
    const savedNeighborhoodZoneId = localStorage.getItem('customer_neighborhood_zone_id');
    const addressNeighborhood = address?.neighborhood;
    // 1) Prioriza match por ID salvo
    let zone = savedNeighborhoodZoneId
      ? deliveryZones.find(z => z.id === savedNeighborhoodZoneId)
      : null;
    // 2) Fallback: match por nome normalizado (localStorage OU address)
    if (!zone) {
      const candidateName = normalize(savedNeighborhood || addressNeighborhood);
      if (candidateName) {
        zone = deliveryZones.find(z => normalize(z.name) === candidateName);
      }
    }
    if (zone) {
      setSelectedZone(zone);
      // Sincronizar tudo com o valor canônico da zona (evita bug de "bairro inválido")
      setAddress(prev => ({ ...prev, neighborhood: zone.name }));
      try {
        localStorage.setItem('customer_neighborhood', zone.name);
        localStorage.setItem('customer_neighborhood_zone_id', zone.id);
      } catch (_) {}
      console.log('🏘️ Bairro sincronizado com zonas carregadas:', zone.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryZones, address?.neighborhood]);

  // Função para forçar refresh do cache
  const refreshCache = async () => {
    try {
      await axios.post(`${API}/public/refresh-cache/${subdomain}`);
      console.log('🔄 Cache refreshed, reloading data...');
      await loadMenuData();
    } catch (error) {
      console.error('Error refreshing cache:', error);
    }
  };

  // 🚀 CACHE LOCAL: Chave única para este restaurante
  const CACHE_KEY = `menu_cache_${subdomain}`;
  const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutos

  // Função para salvar dados no localStorage
  const saveToCache = (data) => {
    try {
      const cacheData = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
      console.log('💾 Menu salvo no cache local');
    } catch (e) {
      console.warn('⚠️ Erro ao salvar cache:', e);
    }
  };

  // Função para carregar dados do cache
  const loadFromCache = () => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const isExpired = Date.now() - timestamp > CACHE_EXPIRY;
        return { data, isExpired };
      }
    } catch (e) {
      console.warn('⚠️ Erro ao ler cache:', e);
    }
    return null;
  };

  // arraysAreEqual -> utils/publicMenuHelpers (importado)

  const loadMenuData = async (isRetry = false) => {
    try {
      // 🚀 OTIMIZAÇÃO 1: Carregar cache local IMEDIATAMENTE
      const cached = loadFromCache();
      if (cached && cached.data) {
        console.log('⚡ Carregando dados do cache local...');
        const { restaurant: cachedRestaurant, categories: cachedCategories, products: cachedProducts, 
                additionals: cachedAdditionals, additionalGroups: cachedAdditionalGroups,
                deliveryZones: cachedZones, layout: cachedLayout, deliveryConfig: cachedDeliveryConfig,
                featuredProducts: cachedFeatured } = cached.data;
        
        // Aplicar dados do cache imediatamente
        // NOTA: Latitude/Longitude podem não estar no cache antigo, então sempre sobrescrever do server
        if (cachedRestaurant) setRestaurant(cachedRestaurant);
        if (cachedCategories) setCategories(prev => arraysAreEqual(prev, cachedCategories) ? prev : cachedCategories);
        if (cachedProducts) setProducts(prev => arraysAreEqual(prev, cachedProducts) ? prev : cachedProducts);
        if (cachedAdditionals) setAdditionals(cachedAdditionals);
        if (cachedAdditionalGroups) setAdditionalGroups(cachedAdditionalGroups);
        if (cachedZones) setDeliveryZones(cachedZones);
        if (cachedLayout) setLayout(cachedLayout);
        if (cachedDeliveryConfig) setDeliveryConfig(cachedDeliveryConfig);
        if (cachedFeatured) setFeaturedProducts(cachedFeatured);
        
        // Se o cache não expirou, apenas atualiza em background
        if (!cached.isExpired) {
          setLoading(false);
          console.log('✅ Cache válido - atualizando em background');
          // Atualiza em background sem mostrar loading
          fetchFreshData(false);
          return;
        } else {
          console.log('⏰ Cache expirado - buscando dados frescos');
        }
      }
      
      setLoading(true);
      await fetchFreshData(true);
      
    } catch (err) {
      console.error('Error loading menu data:', err);
      setError('Erro ao carregar cardápio');
      setLoading(false);
    }
  };

  // Função separada para buscar dados frescos do servidor
  const fetchFreshData = async (showLoading = true) => {
    try {
      // Add timestamp to force cache bypass
      const timestamp = new Date().getTime();
      const cacheHeaders = {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      };
      
      // 🚀 OTIMIZAÇÃO: Carregar TUDO em uma única chamada
      try {
        const fullMenuRes = await axios.get(`${API}/public/menu/${subdomain}/full?t=${timestamp}`, cacheHeaders);
        const data = fullMenuRes.data;
        
        // 🔄 ATUALIZAR RESTAURANT: Sempre pegar dados do server (pode ter coordenadas novas)
        setRestaurant(data.restaurant);
        
        setCategories(prev => arraysAreEqual(prev, data.categories) ? prev : data.categories);
        setProducts(prev => arraysAreEqual(prev, data.products) ? prev : data.products);
        setAdditionals(prev => arraysAreEqual(prev, data.additionals) ? prev : data.additionals);
        setAdditionalGroups(prev => arraysAreEqual(prev, data.additional_groups) ? prev : data.additional_groups);
        setDeliveryZones(prev => arraysAreEqual(prev, data.delivery_zones) ? prev : data.delivery_zones);
        setLayout(prev => prev === (data.layout || 'list') ? prev : (data.layout || 'list'));
        
        console.log('🚀 Menu carregado - atualizações aplicadas apenas se necessário');
        
        // Objeto para salvar no cache
        let cacheData = {
          restaurant: data.restaurant,
          categories: data.categories,
          products: data.products,
          additionals: data.additionals,
          additionalGroups: data.additional_groups,
          deliveryZones: data.delivery_zones,
          layout: data.layout || 'list'
        };
        
        // 🗺️ Carregar configuração de delivery (modo neighborhood vs radius)
        try {
          const configRes = await axios.get(`${API}/public/delivery-config/${subdomain}?t=${timestamp}`, cacheHeaders);
          setDeliveryConfig(configRes.data);
          cacheData.deliveryConfig = configRes.data;
          console.log('🗺️ Delivery config:', configRes.data.mode);
        } catch (configError) {
          console.warn('⚠️ Delivery config não encontrada, usando padrão (neighborhood)');
          const defaultConfig = { mode: 'neighborhood', radius_zones: [], max_delivery_radius: null, has_store_address: false };
          setDeliveryConfig(defaultConfig);
          cacheData.deliveryConfig = defaultConfig;
        }
        
        // Carregar produtos em destaque
        try {
          const featuredRes = await axios.get(`${API}/public/restaurant/${subdomain}/featured-products?t=${timestamp}`, cacheHeaders);
          // 🔄 ANTI-FLICKER: Só atualizar se mudou
          setFeaturedProducts(prev => arraysAreEqual(prev, featuredRes.data || []) ? prev : (featuredRes.data || []));
          cacheData.featuredProducts = featuredRes.data || [];
          console.log(`✨ ${featuredRes.data?.length || 0} produtos em destaque carregados`);
        } catch (featuredError) {
          console.warn('⚠️ Erro ao carregar produtos em destaque:', featuredError.message);
          setFeaturedProducts(prev => prev.length === 0 ? prev : []);
          cacheData.featuredProducts = [];
        }
        
        // 💾 Salvar no cache local
        saveToCache(cacheData);
        
      } catch (fullMenuError) {
        // Fallback para chamadas individuais se endpoint unificado falhar
        console.warn('⚠️ Usando fallback para chamadas individuais:', fullMenuError.message);
        
        // Load restaurant info
        const restaurantRes = await axios.get(`${API}/public/restaurant/${subdomain}?t=${timestamp}`, cacheHeaders);
        setRestaurant(prev => {
          if (prev && restaurantRes.data && prev.id === restaurantRes.data.id) {
            return prev;
          }
          return restaurantRes.data;
        });
        
        // Load layout preference
        try {
          const appearanceRes = await axios.get(`${API}/public/restaurant/${subdomain}/appearance?t=${timestamp}`, cacheHeaders);
          setLayout(prev => prev === (appearanceRes.data.layout || 'list') ? prev : (appearanceRes.data.layout || 'list'));
        } catch (error) {
          setLayout(prev => prev === 'list' ? prev : 'list');
        }
        
        // Carregar resto em paralelo para ser mais rápido
        const [categoriesRes, productsRes, additionalsRes] = await Promise.all([
          axios.get(`${API}/public/menu/${subdomain}/categories?t=${timestamp}`, cacheHeaders),
          axios.get(`${API}/public/menu/${subdomain}/products?t=${timestamp}`, cacheHeaders),
          axios.get(`${API}/public/menu/${subdomain}/additionals?t=${timestamp}`, cacheHeaders)
        ]);
        
        // 🔄 ANTI-FLICKER: Só atualizar se dados mudaram
        setCategories(prev => arraysAreEqual(prev, categoriesRes.data) ? prev : categoriesRes.data);
        setProducts(prev => arraysAreEqual(prev, productsRes.data) ? prev : productsRes.data);
        setAdditionals(prev => arraysAreEqual(prev, additionalsRes.data) ? prev : additionalsRes.data);
        
        // Load additional groups
        try {
          const groupsRes = await axios.get(`${API}/public/menu/${subdomain}/additional-groups?t=${timestamp}`, cacheHeaders);
          setAdditionalGroups(prev => arraysAreEqual(prev, groupsRes.data) ? prev : groupsRes.data);
        } catch (error) {
          setAdditionalGroups(prev => prev.length === 0 ? prev : []);
        }
        
        // Load delivery configuration (mode: neighborhood ou radius)
        try {
          const configRes = await axios.get(`${API}/public/delivery-config/${subdomain}?t=${timestamp}`, cacheHeaders);
          setDeliveryConfig(configRes.data);
        } catch (error) {
          // Fallback para modo padrão (neighborhood)
          setDeliveryConfig({ mode: 'neighborhood', radius_zones: [], max_delivery_radius: null, has_store_address: false });
        }
        
        // Load delivery zones (para modo neighborhood)
        try {
          const zonesRes = await axios.get(`${API}/public/delivery-zones/${subdomain}?t=${timestamp}`, cacheHeaders);
          setDeliveryZones(prev => arraysAreEqual(prev, zonesRes.data) ? prev : zonesRes.data);
        } catch (error) {
          setDeliveryZones(prev => prev.length === 0 ? prev : []);
        }
        
        // Carregar produtos em destaque (fallback)
        try {
          const featuredRes = await axios.get(`${API}/public/restaurant/${subdomain}/featured-products?t=${timestamp}`, cacheHeaders);
          setFeaturedProducts(prev => arraysAreEqual(prev, featuredRes.data || []) ? prev : (featuredRes.data || []));
        } catch (featuredError) {
          setFeaturedProducts(prev => prev.length === 0 ? prev : []);
        }
      }
      
      // Load cashback data if customer phone is available
      const savedPhone = localStorage.getItem('customer_phone');
      if (savedPhone) {
        try {
          const cashbackRes = await axios.get(`${API}/public/cashback/${subdomain}/${savedPhone}?t=${timestamp}`, cacheHeaders);
          setCashbackData(cashbackRes.data);
        } catch (error) {
          console.error('Error loading cashback data:', error);
          // Não mostra erro para o usuário, apenas não carrega o cashback
        }
      }
      
      // 🎟️ Check if restaurant has active coupons
      try {
        const couponsCheckRes = await axios.get(`${API}/public/coupons/active-check/${subdomain}?t=${timestamp}`, cacheHeaders);
        setHasActiveCoupons(couponsCheckRes.data.has_active_coupons || false);
      } catch (error) {
        console.error('Error checking active coupons:', error);
        setHasActiveCoupons(false);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading menu data:', error);
      setLoading(false);
      // Mostrar erro em vez de dados falsos
      setError('Não foi possível carregar o cardápio. Tente novamente.');
    }
  };

  // 📅 Load scheduling slots
  const loadSchedulingSlots = async () => {
    try {
      setLoadingSlots(true);
      const response = await axios.get(`${API}/public/menu/${subdomain}/scheduling-slots`);
      setSchedulingSlots(response.data.slots || []);
      return response.data;
    } catch (error) {
      console.error('Error loading scheduling slots:', error);
      setSchedulingSlots([]);
      return { scheduling_available: false };
    } finally {
      setLoadingSlots(false);
    }
  };

  // Check if scheduling is needed and show modal
  const checkSchedulingRequired = async () => {
    const isClosed = !restaurant?.status?.is_open || restaurant?.manually_closed;
    const schedulingEnabled = restaurant?.scheduling?.enabled;
    const schedulingWhenOpen = restaurant?.scheduling?.when_open;
    
    // If store is closed and scheduling is enabled
    if (isClosed && schedulingEnabled) {
      const slotsData = await loadSchedulingSlots();
      if (slotsData.scheduling_available) {
        setShowSchedulingModal(true);
        return true;
      } else {
        setShowClosedStoreModal(true);
        return true;
      }
    }
    
    // If store is open but scheduling when open is enabled, optionally offer scheduling
    if (!isClosed && schedulingWhenOpen && schedulingEnabled) {
      // Allow immediate order or scheduled
      return false;
    }
    
    return false;
  };

  // Carrinho (addToCart/updateCartItemQuantity/clearCart/getCartTotal/getCartItemsCount) -> usePublicMenuCart

  // Function to handle reordering from order history
  const handleReorder = (order) => {
    if (!order.items || order.items.length === 0) {
      showToast('Não foi possível encontrar os itens deste pedido.', 'error');
      return;
    }

    // Check if restaurant is open or scheduling is available
    const isClosed = !restaurant?.status?.is_open || restaurant?.manually_closed;
    const schedulingEnabled = restaurant?.scheduling?.enabled;
    
    if (isClosed && !schedulingEnabled) {
      setShowClosedStoreModal(true);
      return;
    }

    const unavailableItems = [];
    const itemsToAdd = [];

    // Verify each item from the order
    order.items.forEach(orderItem => {
      const product = products.find(p => p.name === orderItem.product_name && p.available);
      
      if (!product) {
        unavailableItems.push(orderItem.product_name);
      } else {
        // Create cart item for available product
        const cartItem = {
          id: `${product.id}-${Date.now()}-${Math.random()}`,
          product: product,
          additionals: orderItem.additionals || [],
          quantity: orderItem.quantity,
          totalPrice: product.price * orderItem.quantity,
          notes: ''
        };
        itemsToAdd.push(cartItem);
      }
    });

    // Show message if some items are unavailable
    if (unavailableItems.length > 0) {
      if (unavailableItems.length === order.items.length) {
        showToast('😔 Infelizmente, nenhum dos produtos deste pedido está disponível no momento.', 'error');
      } else {
        showToast(`😔 Alguns produtos não estão mais disponíveis: ${unavailableItems.join(', ')}. Os demais itens foram adicionados ao carrinho.`, 'warning');
        setCart([...cart, ...itemsToAdd]);
        setCurrentScreen('cart');
      }
    } else {
      // All items available, add to cart
      setCart([...cart, ...itemsToAdd]);
      showToast('✅ Produtos adicionados ao carrinho com sucesso!', 'success');
      setCurrentScreen('cart');
    }
  };

  // Function to show toast messages
  const showToast = (message, type = 'info') => {
    setToastMessage({ text: message, type });
    setTimeout(() => setToastMessage(null), 4000);
  };

  // getNextOpeningInfo -> utils/publicMenuHelpers (importado, recebe `restaurant`)

  const handleWhatsAppOrder = async () => {
    // Prevenir múltiplos cliques
    if (isSubmittingOrder) {
      return;
    }
    
    try {
      setIsSubmittingOrder(true);
      
      // Check if restaurant is suspended or expired before allowing order
      const isSuspended = restaurant?.status?.is_suspended === true;
      const isExpired = restaurant?.status?.is_expired === true;
      const isClosed = !restaurant?.status?.is_open || restaurant?.manually_closed;
      
      // If store is closed and not a scheduled order, check if scheduling is available
      if ((isSuspended || isExpired) || (isClosed && !isScheduledOrder)) {
        const schedulingEnabled = restaurant?.scheduling?.enabled;
        if (isClosed && schedulingEnabled && !isScheduledOrder) {
          // Show scheduling modal instead
          const slotsData = await loadSchedulingSlots();
          if (slotsData.scheduling_available) {
            setShowSchedulingModal(true);
            setIsSubmittingOrder(false);
            return;
          }
        }
        setShowClosedStoreModal(true);
        setIsSubmittingOrder(false);
        return;
      }
      
      // ===== VALIDAÇÃO OBRIGATÓRIA DO BAIRRO PARA DELIVERY =====
      if (deliveryType === 'delivery') {
        // Verificar se o bairro foi preenchido
        if (!address.neighborhood || address.neighborhood.trim() === '') {
          toast.error('Por favor, informe o bairro de entrega');
          setCheckoutStep(3);
          setIsSubmittingOrder(false);
          return;
        }
        
        // Validação específica por modo de entrega
        if (deliveryConfig.mode === 'neighborhood') {
          // Modo bairro: precisa ter zonas cadastradas e bairro válido
          const validZone = deliveryZones.find(z => z.name === address.neighborhood);
          if (!validZone && deliveryZones.length > 0) {
            toast.error('Bairro inválido. Por favor, selecione um bairro da lista');
            setCheckoutStep(3);
            setIsSubmittingOrder(false);
            return;
          }
          
          if (deliveryZones.length === 0) {
            toast.error('Desculpe, não há bairros disponíveis para entrega no momento');
            setIsSubmittingOrder(false);
            return;
          }
        } else if (deliveryConfig.mode === 'radius') {
          // Modo raio: precisa ter calculado a taxa
          if (!calculatedFee) {
            toast.error('Por favor, calcule a taxa de entrega antes de finalizar');
            setCheckoutStep(3);
            setIsSubmittingOrder(false);
            return;
          }
        }
      }
      // ===== FIM DA VALIDAÇÃO =====
      
      // Pegar valores dos refs
      const finalCustomerName = customerNameRef.current?.value || customerName || 'Cliente Web';
      const finalCustomerPhone = customerPhoneRef.current?.value || customerPhone;
      const finalStreet = streetRef.current?.value || address.street;
      const finalNumber = numberRef.current?.value || address.number;
      const finalComplement = complementRef.current?.value || address.complement;
      
      const subtotal = getCartTotal();
      // Taxa BASE (sem promo) + aplicar promo se configurada
      const baseDeliveryFee = deliveryType === 'delivery'
        ? (deliveryConfig.mode === 'radius'
            ? (calculatedFee?.original_fee ?? calculatedFee?.fee ?? 0)
            : (selectedZone?.fee ?? 0))
        : 0;
      const _promoInfo = applyShippingPromo(baseDeliveryFee, subtotal, deliveryConfig?.shipping_promo, deliveryType);
      const deliveryFee = _promoInfo.finalFee;
      const cashbackUsed = useCashback ? (parseFloat(cashbackAmountRef.current?.value) || cashbackAmount) : 0;
      const couponDiscountUsed = appliedCoupon ? couponDiscount : 0;
      const totalBeforeCashback = subtotal + deliveryFee;
      const finalTotal = totalBeforeCashback - cashbackUsed - couponDiscountUsed;
      
      // Create order data
      const orderData = {
        customer_name: finalCustomerName,
        customer_phone: finalCustomerPhone,
        items: cart.map(item => ({
          product_id: item.product.id,
          product_name: item.product.name,
          quantity: item.quantity,
          price: item.product.price,
          additionals: item.additionals.map(add => ({
            additional_id: add.id,
            additional_name: add.name,
            quantity: add.quantity,
            price: add.price
          })),
          notes: item.notes || ''
        })),
        total: finalTotal,
        subtotal: subtotal,
        delivery_fee: deliveryFee,
        delivery_fee_original: _promoInfo.originalFee,
        shipping_promo_applied: _promoInfo.activeTier ? {
          label: _promoInfo.activeTier.label || null,
          min_subtotal: _promoInfo.activeTier.min_subtotal,
          discount_type: _promoInfo.activeTier.discount_type || 'free',
          discount_value: Number(_promoInfo.activeTier.discount_value || 0),
          savings: _promoInfo.savings,
        } : null,
        delivery_zone_name: deliveryType === 'delivery' ? (selectedZone?.name || address.neighborhood || '') : null,
        cashback_used: cashbackUsed,
        coupon_code: appliedCoupon?.code || null,
        coupon_discount: couponDiscountUsed,
        delivery_type: deliveryType,
        address: deliveryType === 'delivery' ? {
          ...address,
          street: finalStreet,
          number: finalNumber,
          complement: finalComplement,
          // Garantir que o neighborhood venha do selectedZone (modo bairro) ou do address
          neighborhood: selectedZone?.name || address.neighborhood || ''
        } : {},
        payment_method: paymentMethod,
        change_for: paymentMethod === 'dinheiro' && needsChange ? parseFloat(changeAmountRef.current?.value || changeAmount || 0) : null,
        // Scheduling data
        is_scheduled: isScheduledOrder,
        scheduled_date: isScheduledOrder ? selectedScheduleDate : null,
        scheduled_time: isScheduledOrder ? selectedScheduleTime : null
      };

      // Se for PIX Mercado Pago, abrir modal
      if (paymentMethod === 'pix_mp') {
        setPixOrderData(orderData);
        setShowPixCheckout(true);
        setIsSubmittingOrder(false);
        return;
      }

      // 💳 Se for Cartão Online, abrir modal de cartão (cobra antes de criar pedido)
      if (paymentMethod === 'cartao_online') {
        setCardOrderData(orderData);
        setShowCardCheckout(true);
        setIsSubmittingOrder(false);
        return;
      }

      // Para outros métodos de pagamento, criar pedido normalmente
      const orderResponse = await axios.post(`${API}/public/menu/${subdomain}/orders`, orderData);
      const orderNumber = orderResponse.data?.order_number || 'N/A';

      // Generate WhatsApp message
      let message = `🍔 PEDIDO ${restaurant?.name?.toUpperCase() || 'RESTAURANTE'}\n\n`;
      
      // Informações do Cliente
      message += `👤 CLIENTE:\n`;
      message += `Nome: ${finalCustomerName}\n`;
      message += `Telefone: ${finalCustomerPhone}\n\n`;
      
      message += `📝 ITENS DO PEDIDO:\n`;
      
      cart.forEach(item => {
        message += `• ${item.quantity}x ${item.product.name} - R$ ${item.totalPrice.toFixed(2)}\n`;
        item.additionals.forEach(add => {
          if (add.quantity > 0) {
            message += `  - ${add.quantity}x ${add.name}\n`;
          }
        });
        if (item.notes && item.notes.trim() !== '') {
          message += `  📌 Obs: ${item.notes}\n`;
        }
      });

      message += `\n💰 Subtotal: R$ ${subtotal.toFixed(2)}\n`;
      if (deliveryType === 'delivery') {
        message += `🚚 Taxa de entrega: R$ ${deliveryFee.toFixed(2)}\n`;
      }
      if (couponDiscountUsed > 0) {
        message += `🎟️ Cupom ${appliedCoupon.code}: -R$ ${couponDiscountUsed.toFixed(2)}\n`;
      }
      if (cashbackUsed > 0) {
        message += `✨ Cashback aplicado: -R$ ${cashbackUsed.toFixed(2)}\n`;
      }
      message += `💳 TOTAL: R$ ${finalTotal.toFixed(2)}\n\n`;

      if (deliveryType === 'delivery') {
        message += `🚚 Entrega: Delivery\n`;
        message += `📍 Endereço: ${finalStreet}, ${finalNumber} - ${address.neighborhood}\n`;
        if (finalComplement) {
          message += `   Complemento: ${finalComplement}\n`;
        }
      } else {
        message += `🏪 Retirada no Local\n`;
      }

      message += `\n💳 Pagamento: ${paymentMethod === 'dinheiro' ? 'Dinheiro na entrega' : paymentMethod === 'cartao' ? 'Cartão' : 'PIX'}`;
      
      // Adicionar informação de troco se necessário
      if (paymentMethod === 'dinheiro' && needsChange) {
        const trocoValue = changeAmountRef.current?.value || changeAmount;
        if (trocoValue) {
          message += `\n💵 Troco para: R$ ${parseFloat(trocoValue).toFixed(2)}`;
        }
      }
      
      message += `\n\n---\nPedido #${orderNumber} realizado pelo cardápio digital`;

      // Usar telefone do restaurante configurado nas configurações
      const phone = restaurant?.phone;
      
      // Se cashback foi usado, recarregar dados para refletir o novo saldo
      if (cashbackUsed > 0 && finalCustomerPhone) {
        await loadCashbackData(finalCustomerPhone);
      }
      
      // Preparar dados para tela de confirmação
      const confirmationData = {
        orderNumber: orderNumber,
        total: finalTotal,
        whatsappMessage: message,
        restaurantPhone: phone,
        restaurantName: restaurant?.name || 'Restaurante'
      };
      
      // Mostrar tela de confirmação
      setConfirmationOrderData(confirmationData);
      setShowOrderConfirmation(true);
      
      // Se for Android, abrir WhatsApp automaticamente após 1.5 segundos
      if (!isIOS() && phone) {
        setTimeout(() => {
          openWhatsApp(phone, message);
        }, 1500);
      }
      
      // Clear cart and reset states after successful order
      setCart([]);
      setUseCashback(false);
      setCashbackAmount(0);
      removeCoupon(); // Limpar cupom
      setCurrentScreen('home');
      
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Erro ao processar pedido. Tente novamente.');
      setIsSubmittingOrder(false);
    }
  };

  // Group products by category and sort by display_order (if set) then by price (ascending)
  // useMemo: só recalcula quando categories ou products mudam (essencial pra evitar
  // que o LazyImage re-monte em todo render e cause flicker de imagens)
  const groupedProducts = useMemo(() => (
    categories.map(category => ({
      ...category,
      products: products
        .filter(product => product.category_id === category.id)
        .sort((a, b) => {
          const orderA = a.display_order;
          const orderB = b.display_order;
          if (orderA != null && orderA > 0 && orderB != null && orderB > 0) {
            return orderA - orderB;
          }
          if (orderA != null && orderA > 0) return -1;
          if (orderB != null && orderB > 0) return 1;
          const priceA = a.promotional_price ?? a.promo_price ?? a.sale_price ?? a.price;
          const priceB = b.promotional_price ?? b.promo_price ?? b.sale_price ?? b.price;
          if (priceA !== priceB) return priceA - priceB;
          return a.name.localeCompare(b.name, 'pt-BR');
        })
    })).filter(category => category.products.length > 0)
  ), [categories, products]);

  const filteredProducts = groupedProducts;

  // Handle category click - scroll to category section
  const handleCategoryClick = (categoryId) => {
    // Atualiza o estado visual (destaque no carrossel)
    setSelectedCategoryFilter(categoryId);
    
    // Agendar o scroll para depois do re-render
    setTimeout(() => {
      const scrollContainer = document.getElementById('home-scroll-container');
      if (!scrollContainer) {
        console.log('Container não encontrado');
        return;
      }
      
      if (categoryId === 'all') {
        scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const categoryElement = document.getElementById(`category-${categoryId}`);
        if (categoryElement) {
          // Calcular posição usando getBoundingClientRect
          const containerRect = scrollContainer.getBoundingClientRect();
          const elementRect = categoryElement.getBoundingClientRect();
          const currentScroll = scrollContainer.scrollTop;
          
          // Posição relativa + scroll atual - offset para título ficar visível
          const targetPosition = currentScroll + (elementRect.top - containerRect.top) - 160;
          
          console.log('Scrolling to category:', categoryId, 'position:', targetPosition);
          
          scrollContainer.scrollTo({ 
            top: Math.max(0, targetPosition), 
            behavior: 'smooth' 
          });
        } else {
          console.log('Elemento da categoria não encontrado:', categoryId);
        }
      }
    }, 150);
  };

  // Search products function with debouncing
  const handleSearch = (e) => {
    const query = e.target.value;
    
    // Update query state for controlled input
    setSearchQuery(query);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // If query is empty, clear results immediately
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    
    // Debounce the actual search by 300ms
    searchTimeoutRef.current = setTimeout(() => {
      const searchTerm = query.toLowerCase().trim();
      const results = [];
      
      // Search through all products
      categories.forEach(category => {
        const categoryProducts = products.filter(p => p.category_id === category.id);
        
        categoryProducts.forEach(product => {
          // Search in name and description
          if (
            product.name.toLowerCase().includes(searchTerm) ||
            (product.description && product.description.toLowerCase().includes(searchTerm))
          ) {
            results.push({
              ...product,
              categoryName: category.name
            });
          }
        });
      });
      
      // Sort search results by display_order (if set) then by price (ascending)
      results.sort((a, b) => {
        // First check display_order
        const orderA = a.display_order;
        const orderB = b.display_order;
        
        if (orderA != null && orderA > 0 && orderB != null && orderB > 0) {
          return orderA - orderB;
        }
        if (orderA != null && orderA > 0) return -1;
        if (orderB != null && orderB > 0) return 1;
        
        // Then sort by price
        const priceA = a.promotional_price ?? a.promo_price ?? a.sale_price ?? a.price;
        const priceB = b.promotional_price ?? b.promo_price ?? b.sale_price ?? b.price;
        
        if (priceA !== priceB) {
          return priceA - priceB;
        }
        return a.name.localeCompare(b.name, 'pt-BR');
      });
      
      setSearchResults(results);
    }, 300);
  };
  
  // Open search modal
  const openSearch = () => {
    setShowSearchModal(true);
    setSearchQuery('');
    setSearchResults([]);
  };
  
  // Close search modal
  const closeSearch = () => {
    setShowSearchModal(false);
    setSearchQuery('');
    setSearchResults([]);
    // Clear any pending search timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  };

  // Maintain focus on search input when modal is open
  useEffect(() => {
    if (showSearchModal && searchInputRef.current) {
      // Small delay to ensure modal is rendered
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      
      return () => clearTimeout(timer);
    }
  }, [showSearchModal]);

  // Header Component
  // Header extraído (Fase 2 refactor) -> components/publicmenu/Header.js
  // Chamado inline como {Header({ ...props })} para preservar o comportamento original.
  
  // Search Modal Component
  // SearchModal extraido (Fase 2 refactor) -> components/publicmenu/SearchModal.js
  // Renderizado como <SearchModal .../> passando estado/handlers/ref via props.

  // Bottom Navigation - apenas na tela home
  // BottomNav extraído (Fase 2 refactor) -> components/publicmenu/BottomNav.js
  // Chamado inline como {BottomNav({ ...props })} para preservar o comportamento original.

  // Home Screen
  // HomeScreen extraído (Fase 2 refactor) -> components/publicmenu/HomeScreen.js
  // Chamado inline como {HomeScreen({ ...props })} para preservar o comportamento original.

  // Product Detail Screen
  // Product Detail Screen
  // ProductScreen extraido (Fase 2 refactor) -> components/publicmenu/ProductScreen.js
  // Renderizado como <ProductScreen .../> passando estado/handlers via props.

  // Cart Screen
  // CartScreen extraído (Fase 2 refactor) -> components/publicmenu/CartScreen.js
  // Chamado inline como {CartScreen({ ...props })} para preservar o comportamento original.

  // Checkout Screen
  // Checkout Screen
  // CheckoutScreen extraido (Fase 2 refactor) -> components/publicmenu/CheckoutScreen.js
  // Renderizado como <CheckoutScreen .../> passando estado/handlers via props.

  // Cart Confirmation Modal
  // CartConfirmationModal extraído (Fase 2 refactor) -> components/publicmenu/CartConfirmationModal.js
  // Renderizado como <CartConfirmationModal .../> passando estado/handlers via props.

  // Closed Store Modal Component
  // ClosedStoreModal extraído (Fase 2 refactor) -> components/publicmenu/ClosedStoreModal.js
  // Renderizado como <ClosedStoreModal .../> passando estado/handlers via props.

  // 📅 Scheduling Modal Component
  // SchedulingModal extraido (Fase 2 refactor) -> components/publicmenu/SchedulingModal.js
  // Renderizado como <SchedulingModal .../> passando estado/handlers via props.

  // Minimum Order Modal Component - Compacto e Discreto
  // MinimumOrderModal extraído (Fase 2 refactor) -> components/publicmenu/MinimumOrderModal.js
  // Renderizado como <MinimumOrderModal .../> passando estado/handlers via props.

  // Order Details Modal Component
  // OrderDetailsModal extraido (Fase 2 refactor) -> components/publicmenu/OrderDetailsModal.js
  // Renderizado como <OrderDetailsModal .../> passando estado/handlers via props.

  // Promos/Cashback Screen Component  
  // PromosScreen extraído (Fase 2 refactor) -> components/publicmenu/PromosScreen.js
  // Renderizado como <PromosScreen .../> passando estado/handlers via props.


  // Orders History Screen Component
  // OrdersHistoryScreen extraído (Fase 2 refactor) -> components/publicmenu/OrdersHistoryScreen.js
  // Renderizado como <OrdersHistoryScreen .../> passando estado/handlers via props.

  // ✅ Componente: Tela de Confirmação de Pedido
  // OrderConfirmationScreen extraído (Fase 2 refactor) -> components/publicmenu/OrderConfirmationScreen.js
  // Renderizado como <OrderConfirmationScreen .../> passando estado/handlers via props.



  return (
    <div className={`min-h-screen bg-gray-50 ${currentScreen === 'cart' || currentScreen === 'checkout' ? 'max-h-screen overflow-hidden' : 'max-h-screen overflow-hidden'}`}>
      {/* Search Modal */}
      <SearchModal
        showSearchModal={showSearchModal}
        closeSearch={closeSearch}
        searchInputRef={searchInputRef}
        searchQuery={searchQuery}
        handleSearch={handleSearch}
        setSearchQuery={setSearchQuery}
        setSearchResults={setSearchResults}
        searchResults={searchResults}
        navigateToScreen={navigateToScreen}
      />
      
      <style jsx>{`
        @keyframes slow-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: .7; }
        }
        .animate-slow-pulse {
          animation: slow-pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        
        /* Continuous pulse animation for cart icon */
        @keyframes cart-pulse {
          0%, 100% { 
            transform: scale(1);
          }
          50% { 
            transform: scale(1.1);
          }
        }
        
        /* Bounce once animation for success checkmark */
        @keyframes bounce-once {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.2);
          }
          70% {
            transform: scale(0.9);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
        
        .animate-bounce-once {
          animation: bounce-once 0.6s ease-out forwards;
        }
        
        /* Single bounce animation when modal opens */
        @keyframes cart-bounce {
          0% {
            transform: translateY(0) scale(1);
          }
          25% {
            transform: translateY(-20px) scale(1.05);
          }
          50% {
            transform: translateY(0) scale(1);
          }
          65% {
            transform: translateY(-10px) scale(1.02);
          }
          80% {
            transform: translateY(0) scale(1);
          }
          90% {
            transform: translateY(-5px) scale(1.01);
          }
          100% {
            transform: translateY(0) scale(1);
          }
        }
        
        .animate-cart-pulse {
          animation: cart-pulse 1.5s ease-in-out infinite;
        }
        
        .animate-cart-bounce-once {
          animation: cart-bounce 0.8s ease-out;
        }
        
        /* Hide scrollbar for categories */
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        /* Ensure scrolling works on mobile */
        body {
          overflow: hidden;
          position: fixed;
          width: 100%;
          height: 100%;
        }
        
        /* Custom scrollbar for cart */
        .cart-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .cart-scroll::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .cart-scroll::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 10px;
        }
        .cart-scroll::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      `}</style>
      
      {/* 🚀 Header, HomeScreen, CartScreen e BottomNav são chamados como funções (não
          como componentes JSX) porque estão declarados dentro deste componente pai e
          não possuem hooks internos. Ao chamá-los inline ({Fn()}) evitamos o
          unmount/remount de toda a subárvore a cada re-render do pai — o que quebrava o
          pull-to-refresh nativo do celular, causava perda de posição de scroll e piscadas
          de conteúdo quando dados atualizavam em background. */}
      {Header({ restaurant, currentScreen, navigateToScreen, getCartTotal, deliveryType, deliveryConfig, calculatedFee, selectedZone, cart, clearCart, openSearch, setShowStoreProfile, groupedProducts, handleCategoryClick, selectedCategoryFilter })}
      
      {currentScreen === 'home' && HomeScreen({ loading, products, layout, groupedProducts, restaurant, refreshCache, homeScrollTopRef, featuredProducts, navigateToScreen, filteredProducts })}
      {currentScreen === 'product' && selectedProduct && <ProductScreen selectedProduct={selectedProduct} additionalGroups={additionalGroups} expandedImage={expandedImage} setExpandedImage={setExpandedImage} setCurrentScreen={setCurrentScreen} addToCart={addToCart} />}
      {currentScreen === 'cart' && CartScreen({ cart, setCurrentScreen, updateCartItemQuantity, getCartTotal, deliveryConfig, deliveryType, calculatedFee, selectedZone, checkoutScrollTopRef, navigateToScreen })}
      {currentScreen === 'checkout' && <CheckoutScreen address={address} addressDeliveryResult={addressDeliveryResult} appliedCoupon={appliedCoupon} calculatedFee={calculatedFee} cashbackAmount={cashbackAmount} cashbackData={cashbackData} cepRef={cepRef} changeAmount={changeAmount} changeAmountRef={changeAmountRef} checkoutScrollTopRef={checkoutScrollTopRef} checkoutStep={checkoutStep} clearAddressData={clearAddressData} complementRef={complementRef} couponCode={couponCode} couponDiscount={couponDiscount} couponError={couponError} couponLoading={couponLoading} customerName={customerName} customerNameRef={customerNameRef} customerPhone={customerPhone} customerPhoneInput={customerPhoneInput} customerPhoneRef={customerPhoneRef} deliveryConfig={deliveryConfig} deliveryType={deliveryType} deliveryZones={deliveryZones} fetchAddressByCep={fetchAddressByCep} getCartTotal={getCartTotal} handleWhatsAppOrder={handleWhatsAppOrder} hasActiveCoupons={hasActiveCoupons} initialAddressForForm={initialAddressForForm} isScheduledOrder={isScheduledOrder} isSubmittingOrder={isSubmittingOrder} loadCashbackData={loadCashbackData} loadOrderHistory={loadOrderHistory} loadSchedulingSlots={loadSchedulingSlots} loadingCep={loadingCep} needsChange={needsChange} neighborhoodRef={neighborhoodRef} numberRef={numberRef} paymentMethod={paymentMethod} pixCopied={pixCopied} removeCoupon={removeCoupon} restaurant={restaurant} saveAddressData={saveAddressData} saveCustomerData={saveCustomerData} schedulingModalShown={schedulingModalShown} schedulingSlots={schedulingSlots} selectedScheduleDate={selectedScheduleDate} selectedScheduleTime={selectedScheduleTime} selectedZone={selectedZone} setAddress={setAddress} setAddressDeliveryResult={setAddressDeliveryResult} setCalculatedFee={setCalculatedFee} setCashbackAmount={setCashbackAmount} setChangeAmount={setChangeAmount} setCheckoutStep={setCheckoutStep} setCouponCode={setCouponCode} setCouponError={setCouponError} setCustomerName={setCustomerName} setCustomerPhone={setCustomerPhone} setCustomerPhoneInput={setCustomerPhoneInput} setDeliveryType={setDeliveryType} setFeeError={setFeeError} setNeedsChange={setNeedsChange} setPaymentMethod={setPaymentMethod} setPixCopied={setPixCopied} setSchedulingModalShown={setSchedulingModalShown} setSelectedZone={setSelectedZone} setShowClosedStoreModal={setShowClosedStoreModal} setShowCouponInput={setShowCouponInput} setShowMinOrderModal={setShowMinOrderModal} setShowSchedulingModal={setShowSchedulingModal} setUseCashback={setUseCashback} showCouponInput={showCouponInput} streetRef={streetRef} useCashback={useCashback} validateCoupon={validateCoupon} />}
      {currentScreen === 'promos' && <PromosScreen customerPhone={customerPhone} cashbackData={cashbackData} restaurant={restaurant} loadCashbackData={loadCashbackData} setCashbackData={setCashbackData} />}
      {currentScreen === 'orders' && <OrdersHistoryScreen customerPhone={customerPhone} orderHistory={orderHistory} setSelectedOrderDetails={setSelectedOrderDetails} handleReorder={handleReorder} />}
      
      {BottomNav({ currentScreen, navigateToScreen, cashbackData, getCartItemsCount })}
      
      {/* Store Profile Modal */}
      {showStoreProfile && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center sm:justify-center">
          <div className="bg-white w-full sm:w-[500px] sm:rounded-t-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center">
              <button 
                onClick={() => setShowStoreProfile(false)}
                className="p-2 -ml-2 hover:bg-gray-100 rounded-full"
              >
                <ArrowLeft size={24} className="text-gray-700" />
              </button>
              <h2 className="text-lg font-semibold text-gray-900 ml-3">Sobre o estabelecimento</h2>
            </div>

            {/* Store Name */}
            <div className="px-4 py-6 border-b border-gray-200">
              <h1 className="text-3xl font-bold text-gray-900">{restaurant?.name}</h1>
            </div>

            {/* Operating Hours */}
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700">Horário de atendimento</h3>
            </div>
            <div className="px-4 py-4 border-b border-gray-200">
              {restaurant?.schedule?.auto_open_close ? (
                <div className="space-y-2">
                  {Object.entries({
                    sunday: 'Domingo',
                    monday: 'Segunda-feira',
                    tuesday: 'Terça-feira',
                    wednesday: 'Quarta-feira',
                    thursday: 'Quinta-feira',
                    friday: 'Sexta-feira',
                    saturday: 'Sábado'
                  }).map(([day, label]) => {
                    const daySchedule = restaurant.schedule.schedule?.[day];
                    return (
                      <div key={day} className="flex justify-between text-sm">
                        <span className="text-gray-700">{label}</span>
                        <span className="text-gray-900 font-medium">
                          {daySchedule?.enabled 
                            ? `${daySchedule.open} às ${daySchedule.close}` 
                            : 'Fechado'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-600">Horários não configurados</p>
              )}
            </div>

            {/* Delivery Information */}
            {(restaurant?.delivery_time || restaurant?.minimum_order) && (
              <>
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700">Informações de entrega</h3>
                </div>
                <div className="px-4 py-4 border-b border-gray-200">
                  <div className="space-y-3">
                    {restaurant?.delivery_time && (
                      <div className="flex items-center text-sm text-gray-900">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div>
                          <span className="font-medium">Tempo de entrega:</span>
                          <span className="ml-2">{restaurant.delivery_time}</span>
                        </div>
                      </div>
                    )}
                    {restaurant?.minimum_order > 0 && (
                      <div className="flex items-center text-sm text-gray-900">
                        <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center mr-3">
                          <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <div>
                          <span className="font-medium">Pedido mínimo:</span>
                          <span className="ml-2">R$ {restaurant.minimum_order?.toFixed(2)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Payment Methods */}
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700">Formas de pagamento</h3>
            </div>
            <div className="px-4 py-4 border-b border-gray-200">
              {restaurant?.payment_methods ? (
                <div className="space-y-3">
                  {restaurant.payment_methods.pix && (
                    <div className="flex items-center text-sm text-gray-900">
                      <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-teal-600 font-bold text-xs">PIX</span>
                      </div>

                      <span>Pix</span>
                    </div>
                  )}
                  {restaurant.payment_methods.cartao && (
                    <div className="flex items-center text-sm text-gray-900">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"/>
                          <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd"/>
                        </svg>
                      </div>
                      <span>Cartão de Crédito/Débito</span>
                    </div>
                  )}
                  {restaurant.payment_methods.dinheiro && (
                    <div className="flex items-center text-sm text-gray-900">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-green-600 font-bold text-lg">$</span>
                      </div>
                      <span>Dinheiro</span>
                    </div>
                  )}
                  {restaurant.payment_methods.vale_refeicao && (
                    <div className="flex items-center text-sm text-gray-900">
                      <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-orange-600 font-bold text-xs">VR</span>
                      </div>
                      <span>Vale Refeição</span>
                    </div>
                  )}
                  {restaurant.payment_methods.vale_alimentacao && (
                    <div className="flex items-center text-sm text-gray-900">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-purple-600 font-bold text-xs">VA</span>
                      </div>
                      <span>Vale Alimentação</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-600">Informações de pagamento não configuradas</p>
              )}
            </div>

            {/* Contact Information */}
            {restaurant?.phone && (
              <>
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700">Contato</h3>
                </div>
                <div className="px-4 py-4 border-b border-gray-200">
                  <a 
                    href={`https://wa.me/${restaurant.phone.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between text-sm text-gray-900 hover:bg-gray-50 -mx-4 px-4 py-3 transition-colors"
                  >
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                      </div>
                      <span>{restaurant.phone}</span>
                    </div>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </a>
                </div>
              </>
            )}

            {/* Close Button */}
            <div className="px-4 py-6">
              <button
                onClick={() => setShowStoreProfile(false)}
                className="w-full bg-primary hover:bg-primary-hover text-white py-3 rounded-lg font-medium transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
      
      <CartConfirmationModal
        showCartConfirmation={showCartConfirmation}
        setShowCartConfirmation={setShowCartConfirmation}
        setCurrentScreen={setCurrentScreen}
      />
      <ClosedStoreModal
        showClosedStoreModal={showClosedStoreModal}
        getNextOpeningInfo={() => getNextOpeningInfo(restaurant)}
        restaurant={restaurant}
        setShowClosedStoreModal={setShowClosedStoreModal}
        setShowStoreProfile={setShowStoreProfile}
        setCurrentScreen={setCurrentScreen}
      />
      <SchedulingModal
        showSchedulingModal={showSchedulingModal}
        selectedScheduleDate={selectedScheduleDate}
        selectedScheduleTime={selectedScheduleTime}
        schedulingSlots={schedulingSlots}
        loadingSlots={loadingSlots}
        setIsScheduledOrder={setIsScheduledOrder}
        setShowSchedulingModal={setShowSchedulingModal}
        setCurrentScreen={setCurrentScreen}
        setSelectedScheduleDate={setSelectedScheduleDate}
        setSelectedScheduleTime={setSelectedScheduleTime}
        setSchedulingModalShown={setSchedulingModalShown}
      />
      <MinimumOrderModal
        showMinOrderModal={showMinOrderModal}
        restaurant={restaurant}
        getCartTotal={getCartTotal}
        setShowMinOrderModal={setShowMinOrderModal}
        setCurrentScreen={setCurrentScreen}
      />
      <OrderDetailsModal
        selectedOrderDetails={selectedOrderDetails}
        setSelectedOrderDetails={setSelectedOrderDetails}
      />
      <OrderConfirmationScreen showOrderConfirmation={showOrderConfirmation} confirmationOrderData={confirmationOrderData} setShowOrderConfirmation={setShowOrderConfirmation} setConfirmationOrderData={setConfirmationOrderData} setIsSubmittingOrder={setIsSubmittingOrder} />
      
      {/* PIX Checkout Modal */}
      <PixCheckoutModal 
        isOpen={showPixCheckout}
        onClose={() => {
          setShowPixCheckout(false);
          setPixOrderData(null);
        }}
        orderData={pixOrderData}
        subdomain={subdomain}
        onSuccess={(confirmedOrder) => {
          // Salvar dados do pedido confirmado para a tela de sucesso
          setSuccessOrderData(confirmedOrder || pixOrderData);
          
          // Limpar carrinho e estados
          setCart([]);
          setUseCashback(false);
          setCashbackAmount(0);
          setShowPixCheckout(false);
          
          // Mostrar tela de sucesso ao invés de voltar para home
          setShowPaymentSuccess(true);
          
          // Reload cashback data if customer phone is available
          if (pixOrderData?.customer_phone) {
            loadCashbackData(pixOrderData.customer_phone);
          }
        }}
      />

      {/* 💳 Card Checkout Modal */}
      {showCardCheckout && cardOrderData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto" data-testid="card-checkout-modal">
          <div className="bg-gray-900 rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-white/10 my-8">
            <div className="p-6 max-h-[90vh] overflow-y-auto">
              {(() => {
                const cleanPhone = (cardOrderData.customer_phone || '').replace(/\D/g, '');
                const fallbackEmail = cleanPhone ? `${cleanPhone}@cliente.pedefacil.app` : 'cliente@pedefacil.app';
                const initialEmail = cardOrderData.customer_email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cardOrderData.customer_email)
                  ? cardOrderData.customer_email
                  : fallbackEmail;
                return (
                  <MPCardCheckout
                    subdomain={subdomain}
                    amount={cardOrderData.total || 0}
                    orderId={null}
                    payerEmail={initialEmail}
                    payerName={cardOrderData.customer_name}
                    onCancel={() => {
                      setShowCardCheckout(false);
                      setCardOrderData(null);
                    }}
                    onSuccess={async (paymentInfo) => {
                      try {
                        const finalOrderData = {
                          ...cardOrderData,
                          payment_method: 'cartao_online',
                          payment_status: 'approved',
                          mp_payment_id: String(paymentInfo.payment_id),
                          card_payment_id: String(paymentInfo.payment_id),
                          installments: paymentInfo.installments || 1,
                          total: cardOrderData.total || paymentInfo.amount,
                        };
                        const orderResponse = await axios.post(
                          `${API}/public/menu/${subdomain}/orders`,
                          finalOrderData
                        );
                        // Merge da resposta do backend + dados locais (backend pode não devolver total/installments)
                        const mergedOrder = {
                          ...finalOrderData,
                          ...(orderResponse.data || {}),
                          total: (orderResponse.data?.total) || finalOrderData.total,
                          installments: (orderResponse.data?.installments) || finalOrderData.installments,
                          payment_method: 'cartao_online',
                        };
                        setSuccessOrderData(mergedOrder);
                        setCart([]);
                        setUseCashback(false);
                        setCashbackAmount(0);
                        setShowCardCheckout(false);
                        setCardOrderData(null);
                        setShowPaymentSuccess(true);
                        if (cardOrderData?.customer_phone) {
                          loadCashbackData(cardOrderData.customer_phone);
                        }
                      } catch (e) {
                        alert('Pagamento aprovado, mas ocorreu erro ao salvar pedido. Contate a loja informando o ID: ' + paymentInfo.payment_id);
                      }
                    }}
                    onPending={(paymentInfo) => {
                      alert('Pagamento em análise. Você receberá uma confirmação em breve.');
                      setShowCardCheckout(false);
                      setCardOrderData(null);
                    }}
                    onError={(msg) => {
                      // Mantém modal aberto pra tentar novamente
                    }}
                  />
                );
              })()}
            </div>
          </div>
        </div>
      )}
      {/* 🎉 Payment Success Modal */}
      {showPaymentSuccess && successOrderData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-scale-in">
            {/* Success Header */}
            <div className="bg-gradient-to-br from-green-400 to-green-600 p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-white rounded-full flex items-center justify-center shadow-lg animate-bounce-once">
                <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Pagamento Confirmado!</h2>
              <p className="text-green-100 text-sm">Seu pedido foi recebido com sucesso</p>
            </div>
            
            {/* Order Summary */}
            <div className="p-6">
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-gray-600 text-sm">Pedido</span>
                  <span className="font-bold text-gray-800">#{successOrderData.order_number || '---'}</span>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-gray-600 text-sm">Total Pago</span>
                  <span className="font-bold text-green-600 text-lg">
                    R$ {Number(
                      successOrderData.total ??
                      successOrderData.total_amount ??
                      successOrderData.amount ??
                      0
                    ).toFixed(2).replace('.', ',')}
                  </span>
                </div>
                <div className="border-t border-gray-200 pt-3 mt-3">
                  <p className="text-xs text-gray-500 text-center">
                    {(() => {
                      const pm = successOrderData.payment_method;
                      if (pm === 'cartao_online') {
                        const inst = successOrderData.installments || successOrderData.card_installments;
                        return `💳 Cartão aprovado via Mercado Pago${inst && inst > 1 ? ` • ${inst}x` : ''}`;
                      }
                      if (pm === 'pix_mp') return '📱 PIX aprovado via Mercado Pago';
                      if (pm === 'pix') return '🔑 PIX confirmado';
                      if (pm === 'dinheiro') return '💵 Pagamento em dinheiro na entrega';
                      if (pm === 'cartao') return '💳 Cartão na entrega (maquininha)';
                      return '✅ Pagamento registrado';
                    })()}
                  </p>
                </div>
              </div>
              
              {/* WhatsApp Button */}
              <button
                onClick={() => {
                  // Construir mensagem resumida para WhatsApp
                  const order = successOrderData;
                  let message = `✅ *PAGAMENTO CONFIRMADO*\n\n`;
                  message += `📝 *Pedido #${order.order_number || '---'}*\n`;
                  message += `👤 *Cliente:* ${order.customer_name || 'Cliente'}\n`;
                  message += `📞 *Telefone:* ${order.customer_phone || '-'}\n\n`;
                  
                  message += `🛒 *Itens:*\n`;
                  (order.items || []).forEach(item => {
                    message += `• ${item.quantity}x ${item.product_name || item.name}\n`;
                  });
                  
                  message += `\n💰 *Total:* R$ ${(order.total || 0).toFixed(2)}`;
                  message += `\n💳 *Pagamento:* PIX (Aprovado ✓)`;
                  
                  if (order.delivery_type === 'delivery' && order.address) {
                    const addr = order.address;
                    message += `\n\n📍 *Entregar em:*\n`;
                    message += `${addr.street || ''}, ${addr.number || ''}`;
                    if (addr.complement) message += ` - ${addr.complement}`;
                    if (addr.neighborhood) message += `\n${addr.neighborhood}`;
                  } else if (order.delivery_type === 'pickup') {
                    message += `\n\n🏪 *Retirada no local*`;
                  }
                  
                  message += `\n\n---\n✨ Pedido pago pelo cardápio digital`;
                  
                  // Abrir WhatsApp
                  const phone = restaurant?.phone;
                  if (phone) {
                    const whatsappUrl = `https://wa.me/${phone.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`;
                    window.open(whatsappUrl, '_blank');
                  } else {
                    showToast('WhatsApp do estabelecimento não configurado', 'warning');
                  }
                }}
                className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-4 px-6 rounded-xl flex items-center justify-center gap-3 transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg"
                data-testid="whatsapp-success-btn"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Enviar Pedido no WhatsApp
              </button>
              
              {/* Secondary Button - Back to Menu */}
              <button
                onClick={() => {
                  setShowPaymentSuccess(false);
                  setSuccessOrderData(null);
                  setPixOrderData(null);
                  setCurrentScreen('home');
                }}
                className="w-full mt-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-6 rounded-xl transition-colors"
                data-testid="back-to-menu-btn"
              >
                Voltar ao Cardápio
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Toast Notification */}
      {toastMessage && (
        <div 
          className={`fixed bottom-20 left-4 right-4 z-50 mx-auto max-w-md animate-slide-up`}
          data-testid="toast-message"
        >
          <div className={`rounded-lg shadow-lg p-4 flex items-center gap-3 ${
            toastMessage.type === 'success' ? 'bg-green-500 text-white' :
            toastMessage.type === 'error' ? 'bg-red-500 text-white' :
            toastMessage.type === 'warning' ? 'bg-orange-500 text-white' :
            'bg-primary text-white'
          }`}>
            <div className="flex-1">
              <p className="text-sm font-medium">{toastMessage.text}</p>
            </div>
            <button
              onClick={() => setToastMessage(null)}
              className="flex-shrink-0 text-white hover:opacity-80 transition-opacity"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicMenu;
